import { Button as ButtonComponent } from './Button';
import { ButtonAdd, ButtonAddLink } from './ButtonAdd';
import { ButtonDelete } from './ButtonDelete';
import { ButtonDownload, ButtonDownloadLink } from './ButtonDownload';
import { ButtonEdit, ButtonEditLink } from './ButtonEdit';
import { ButtonIcon, ButtonIconLink } from './ButtonIcon';
import { ButtonInline } from './ButtonInline';
import { ButtonLink } from './ButtonLink';
import { ButtonOpenInNewTab } from './ButtonOpenInNewTab';
import { ButtonRefresh, ButtonRefreshLink } from './ButtonRefresh';
import { ButtonSelection } from './ButtonSelection';
import { ButtonView, ButtonViewLink } from './ButtonView';

export type { SelectionButtonProps } from './ButtonSelection';
export type {
    AddButtonLinkProps,
    AddButtonProps,
    ButtonLinkProps,
    ButtonProps,
    ButtonSize,
    ButtonType,
    DeleteButtonProps,
    DownloadButtonProps,
    EditButtonProps,
    GenericIconButtonLinkProps,
    IconButtonColor,
    IconButtonLinkProps,
    IconButtonProps,
    IconPosition,
    InlineButtonProps,
    OpenInNewTabLinkProps,
    RefreshButtonProps,
    ViewButtonProps,
} from './types';

/**
 * Icon button with nested .Link subcomponent for type-safe routing
 */
type ButtonAddWithSubcomponents = typeof ButtonAdd & {
    Link: typeof ButtonAddLink;
};

type ButtonViewWithSubcomponents = typeof ButtonView & {
    Link: typeof ButtonViewLink;
};

type ButtonEditWithSubcomponents = typeof ButtonEdit & {
    Link: typeof ButtonEditLink;
};

type ButtonDownloadWithSubcomponents = typeof ButtonDownload & {
    Link: typeof ButtonDownloadLink;
};

type ButtonRefreshWithSubcomponents = typeof ButtonRefresh & {
    Link: typeof ButtonRefreshLink;
};

type ButtonIconWithSubcomponents = typeof ButtonIcon & {
    Link: typeof ButtonIconLink;
};

/**
 * Compound component type that includes both the main button
 * and all subcomponents (Delete, Download, Edit, OpenInNewTab, Refresh, Selection, View, Icon, Inline, Link)
 */
type ButtonWithSubcomponents = typeof ButtonComponent & {
    Add: ButtonAddWithSubcomponents;
    Delete: typeof ButtonDelete;
    Download: ButtonDownloadWithSubcomponents;
    Edit: ButtonEditWithSubcomponents;
    OpenInNewTab: typeof ButtonOpenInNewTab;
    Refresh: ButtonRefreshWithSubcomponents;
    Selection: typeof ButtonSelection;
    View: ButtonViewWithSubcomponents;
    Icon: ButtonIconWithSubcomponents;
    Inline: typeof ButtonInline;
    Link: typeof ButtonLink;
};

/**
 * Modern Button component with HTML <button> base
 *
 * Uses the Modern Minimal design system for styling.
 * Includes compound components for common patterns.
 *
 * @example
 * // Primary button with icon
 * <Button type="primary" icon={<Plus />}>
 *   Create New
 * </Button>
 *
 * @example
 * // Table row actions - button mode (onClick)
 * <Button.View onClick={() => openDrawer()} />
 * <Button.Edit onClick={() => handleEdit(record)} />
 * <Button.Delete onConfirm={handleDelete} />
 * <Button.Refresh onClick={refetch} loading={isRefetching} />
 *
 * @example
 * // Table row actions - router mode (.Link) with full type safety
 * <Button.View.Link to="/claims/$claimId" params={{ claimId: '123' }} />
 * <Button.Edit.Link to="/claims/$claimId/edit" params={{ claimId: '123' }} />
 * <Button.Refresh.Link to="/claims" />
 *
 * @example
 * // Open in new tab (router link + target="_blank")
 * <Button.OpenInNewTab to="/claims/$claimId" params={{ claimId: '123' }} tooltip="View" />
 *
 * @example
 * // Bulk action on a list with row selection: disabled with a hint until rows are selected
 * <Button.Selection selectedCount={selectedRowKeys.length} action="Reassign" noun="claims" nounSingular="claim" onClick={open} />
 *
 * @example
 * // Coloured icon button
 * <Button.Icon icon={<Star />} color="gold" />
 * <Button.Icon icon={<Settings />} color="blue" bordered />
 *
 * @example
 * // Coloured icon button with type-safe router link
 * <Button.Icon.Link icon={<Star />} color="gold" to="/favourites" />
 *
 * @example
 * // A text action inside a line of text, as tall as the text
 * <Button.Inline onClick={openChooser}>Change</Button.Inline>
 *
 * @example
 * // Type-safe navigation link styled as button
 * <Button.Link to="/claims/$claimId" params={{ claimId: '123' }} variant="primary">
 *   View Claim
 * </Button.Link>
 */
// Create nested compound components
const ButtonAddWithLink = Object.assign(ButtonAdd, {
    Link: ButtonAddLink,
}) as ButtonAddWithSubcomponents;

const ButtonViewWithLink = Object.assign(ButtonView, {
    Link: ButtonViewLink,
}) as ButtonViewWithSubcomponents;

const ButtonEditWithLink = Object.assign(ButtonEdit, {
    Link: ButtonEditLink,
}) as ButtonEditWithSubcomponents;

const ButtonDownloadWithLink = Object.assign(ButtonDownload, {
    Link: ButtonDownloadLink,
}) as ButtonDownloadWithSubcomponents;

const ButtonRefreshWithLink = Object.assign(ButtonRefresh, {
    Link: ButtonRefreshLink,
}) as ButtonRefreshWithSubcomponents;

const ButtonIconWithLink = Object.assign(ButtonIcon, {
    Link: ButtonIconLink,
}) as ButtonIconWithSubcomponents;

const Button = Object.assign(ButtonComponent, {
    Add: ButtonAddWithLink,
    Delete: ButtonDelete,
    Download: ButtonDownloadWithLink,
    Edit: ButtonEditWithLink,
    OpenInNewTab: ButtonOpenInNewTab,
    Refresh: ButtonRefreshWithLink,
    Selection: ButtonSelection,
    View: ButtonViewWithLink,
    Icon: ButtonIconWithLink,
    Inline: ButtonInline,
    Link: ButtonLink,
}) as ButtonWithSubcomponents;

export { Button, ButtonOpenInNewTab };
